import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProgressBar, StatRow, CTAButton, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatInt, formatDubaiDate } from '../shared/format';

/**
 * Module 2 — Inline detail sheet (expand inside a bucket row).
 * Shows progress bars per milestone (T+14, T+30, Graduation).
 */
const T14_THRESHOLD_DAYS = 5;
const T30_THRESHOLD_DAYS = 15;
const T30_THRESHOLD_HOURS = 45;

const Milestone = ({ label, progress, target, hint, t }) => (
  <div style={{ marginBottom: 12 }}>
    <StatRow
      label={label}
      hint={hint}
      value={
        <span style={{ fontSize: 13, color: TCN.textDim }}>
          {progress != null && target != null
            ? `${progress} / ${target}`
            : t('tcn.m2.detail.awaiting', 'Awaiting data')}
        </span>
      }
      mono
    />
    <ProgressBar
      value={progress ?? 0}
      target={target || 1}
      height={4}
    />
  </div>
);

const CreatorDetailSheet = ({ creator, onCreateActionPlan }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!creator) return null;

  const isAtRisk =
    creator.t14_status === 'At risk' ||
    creator.t14_status === 'Missed' ||
    creator.t30_status === 'At risk' ||
    creator.t30_status === 'Missed' ||
    creator.graduation_status === 'At risk';

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <Milestone
          label={t('tcn.m2.detail.t14Label', 'T+14: {{count}} valid LIVE days', {
            count: creator.live_days_in_first_14 ?? 0,
          })}
          hint={t('tcn.m2.detail.t14Hint', 'Need ≥ {{n}} valid days', {
            n: T14_THRESHOLD_DAYS,
          })}
          progress={creator.live_days_in_first_14}
          target={T14_THRESHOLD_DAYS}
          t={t}
        />
        <Milestone
          label={t('tcn.m2.detail.t30DaysLabel', 'T+30: valid LIVE days')}
          hint={t('tcn.m2.detail.t30DaysHint', 'Need ≥ {{n}} valid days', {
            n: T30_THRESHOLD_DAYS,
          })}
          progress={creator.live_days_in_first_30}
          target={T30_THRESHOLD_DAYS}
          t={t}
        />
        <Milestone
          label={t('tcn.m2.detail.t30HoursLabel', 'T+30: LIVE hours')}
          hint={t('tcn.m2.detail.t30HoursHint', 'Need ≥ {{h}} hours', {
            h: T30_THRESHOLD_HOURS,
          })}
          progress={
            typeof creator.live_hours_in_first_30 === 'number'
              ? Math.round(creator.live_hours_in_first_30 * 10) / 10
              : null
          }
          target={T30_THRESHOLD_HOURS}
          t={t}
        />
      </div>

      <StatRow
        label={t('tcn.m2.detail.daysSince', 'Days since joining')}
        value={formatInt(creator.days_since_joining)}
        mono
        divider
      />
      {creator.next_milestone_date && (
        <StatRow
          label={t('tcn.m2.detail.nextMilestone', 'Next milestone')}
          value={formatDubaiDate(creator.next_milestone_date)}
          divider
        />
      )}
      <StatRow
        label={t('tcn.m2.detail.gradStatus', 'Graduation status')}
        value={creator.graduation_status || '—'}
        divider
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <CTAButton
          variant="secondary"
          onClick={() => navigate(`/tcn/me/creators/${creator.creator_id}`)}
          fullWidth
          trailing={<Icon name="arrow-right" size={16} />}
        >
          {t('tcn.m2.detail.openProfile', 'Open profile')}
        </CTAButton>
        {isAtRisk && (
          <CTAButton
            variant="primary"
            onClick={() => onCreateActionPlan?.(creator)}
            fullWidth
            leading={<Icon name="plus" size={16} />}
          >
            {t('tcn.m2.detail.createActionPlan', 'Create action plan')}
          </CTAButton>
        )}
      </div>
    </div>
  );
};

export default CreatorDetailSheet;
